// Provisioning wizard — the reseller-facing core flow (doc 03 §3.3).
//
// Site -> channels -> profile (with a live WAN estimate) -> outputs -> review.
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api, type Channel, type EndpointDetail, type Page, type Site } from "../api";
import { Badge, Field, Modal, StatusDot, useAction } from "../ui";

export interface OutputSpec {
  type: "srt" | "hls" | "qam";
  channel_id: number | null;
  params: Record<string, unknown>;
  enabled: boolean;
}

const MAX_CHANNELS = 40;

export default function ProvisionWizard({
  endpoint,
  onClose,
  onDone,
}: {
  endpoint: EndpointDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [step, setStep] = useState(0);
  const [siteId, setSiteId] = useState(endpoint.site_id ?? 0);
  const [profile, setProfile] = useState<"hd" | "sd">(endpoint.default_profile);
  const [selected, setSelected] = useState<Map<number, "hd" | "sd" | null>>(
    new Map(endpoint.entitlements.map((e) => [e.channel_id, e.profile])),
  );
  const [search, setSearch] = useState("");
  const [outputs, setOutputs] = useState<OutputSpec[]>(
    endpoint.outputs.map((o) => ({
      type: o.type,
      channel_id: o.channel_id,
      params: o.params_json,
      enabled: o.enabled,
    })),
  );
  const { run, busy } = useAction();

  const { data: sites } = useQuery<Page<Site>>({ queryKey: ["sites"], queryFn: () => api.get("/v1/sites") });
  const { data: channels } = useQuery<Page<Channel>>({
    queryKey: ["channels"],
    queryFn: () => api.get("/v1/channels?status=active"),
  });

  // Memoised so the WAN estimate below does not recompute on every render.
  const catalog = useMemo(() => channels?.items ?? [], [channels]);
  const filtered = catalog.filter(
    (c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || String(c.number).includes(search),
  );

  // Live WAN estimate from the channel table's nominal_kbps (doc 03 §3.3 step 4),
  // with the same 12 % overhead the API applies so the two never disagree.
  const estimate = useMemo(() => {
    let kbps = 0;
    for (const [channelId, override] of selected) {
      const channel = catalog.find((c) => c.id === channelId);
      if (!channel) continue;
      const effective = override ?? profile;
      kbps += channel.nominal_kbps_json[effective] ?? 0;
    }
    return { kbps, mbps: Math.round((kbps * 1.12) / 1000) };
  }, [selected, profile, catalog]);

  const toggle = (channel: Channel) => {
    const next = new Map(selected);
    if (next.has(channel.id)) next.delete(channel.id);
    else if (next.size < MAX_CHANNELS) next.set(channel.id, null);
    setSelected(next);
  };

  const steps = ["Site", "Channels", "Outputs", "Review"];

  const submit = () =>
    run(async () => {
      await api.post(`/v1/endpoints/${endpoint.id}/provision`, {
        site_id: siteId,
        profile,
        channels: [...selected.entries()].map(([channel_id, override]) => ({
          channel_id,
          profile: override,
        })),
        outputs: outputs.filter((o) => o.enabled),
      });
      onDone();
    }, "Provisioned — the Outpost picks this up on its next check-in");

  return (
    <Modal
      title={`Provision ${endpoint.service_tag}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn-secondary" onClick={() => (step === 0 ? onClose() : setStep(step - 1))}>
            {step === 0 ? "Cancel" : "Back"}
          </button>
          {step < steps.length - 1 ? (
            <button
              className="btn-primary"
              disabled={(step === 0 && !siteId) || (step === 1 && selected.size === 0)}
              onClick={() => setStep(step + 1)}
            >
              Next
            </button>
          ) : (
            <button className="btn-primary" disabled={busy} onClick={submit}>
              {busy ? "Activating…" : "Activate"}
            </button>
          )}
        </>
      }
    >
      <nav className="mb-5 flex gap-2 text-xs">
        {steps.map((label, index) => (
          <span
            key={label}
            className={
              index === step
                ? "rounded-full bg-brand px-3 py-1 font-medium text-ink-900"
                : index < step
                  ? "rounded-full bg-ink-600 px-3 py-1 text-ink-200"
                  : "rounded-full border border-ink-600 px-3 py-1 text-ink-400"
            }
          >
            {index + 1}. {label}
          </span>
        ))}
      </nav>

      {step === 0 && (
        <div className="space-y-4">
          <Field
            label="Customer site"
            hint="The site ID pairs with the service tag as the provisioning key, and survives a hardware swap."
          >
            <select className="input" value={siteId} onChange={(e) => setSiteId(Number(e.target.value))}>
              <option value={0}>Select a site…</option>
              {sites?.items.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                  {site.customer_name ? ` — ${site.customer_name}` : ""}
                  {site.code ? ` (${site.code})` : ""}
                </option>
              ))}
            </select>
          </Field>
          <p className="text-xs text-ink-400">
            Need a new one? Create it on the Sites page first — sites are first-class records with their own
            contact and address details.
          </p>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <input
              className="input max-w-xs"
              placeholder="Search channels…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex items-center gap-3 text-sm">
              <span className={selected.size >= MAX_CHANNELS ? "text-state-warn" : "text-ink-300"}>
                {selected.size} / {MAX_CHANNELS} channels
              </span>
              <select
                className="input w-auto"
                value={profile}
                onChange={(e) => setProfile(e.target.value as "hd" | "sd")}
              >
                <option value="hd">Default profile: hd</option>
                <option value="sd">Default profile: sd</option>
              </select>
            </div>
          </div>

          <div className="rounded-md border border-brand/30 bg-brand/10 px-4 py-3 text-sm">
            <span className="text-ink-200">Estimated WAN required: </span>
            <span className="font-semibold text-brand-on">{estimate.mbps} Mbps</span>
            <span className="ml-2 text-xs text-ink-400">
              ({estimate.kbps.toLocaleString()} kbps nominal + HLS overhead)
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto rounded-md border border-ink-600">
            <table className="table">
              <thead className="sticky top-0 bg-ink-800">
                <tr>
                  <th className="w-10" />
                  <th>#</th>
                  <th>Channel</th>
                  <th>Profile</th>
                  <th>hd</th>
                  <th>sd</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((channel) => {
                  const chosen = selected.has(channel.id);
                  const override = selected.get(channel.id);
                  return (
                    <tr key={channel.id} className={chosen ? "bg-brand/5" : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          className="accent-brand"
                          checked={chosen}
                          onChange={() => toggle(channel)}
                        />
                      </td>
                      <td className="tabular-nums">{channel.number}</td>
                      <td>{channel.name}</td>
                      <td>
                        {chosen && (
                          <select
                            className="input w-auto py-1 text-xs"
                            value={override ?? ""}
                            onChange={(e) => {
                              const next = new Map(selected);
                              next.set(channel.id, (e.target.value || null) as "hd" | "sd" | null);
                              setSelected(next);
                            }}
                          >
                            <option value="">default ({profile})</option>
                            {channel.nominal_kbps_json.hd != null && <option value="hd">hd</option>}
                            {channel.nominal_kbps_json.sd != null && <option value="sd">sd</option>}
                          </select>
                        )}
                      </td>
                      <td className="tabular-nums text-xs text-ink-400">
                        {channel.nominal_kbps_json.hd ?? "—"}
                      </td>
                      <td className="tabular-nums text-xs text-ink-400">
                        {channel.nominal_kbps_json.sd ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 2 && <OutputsStep outputs={outputs} setOutputs={setOutputs} selected={[...selected.keys()]} catalog={catalog} />}

      {step === 3 && (
        <div className="space-y-4 text-sm">
          <dl className="grid grid-cols-2 gap-4">
            <Summary label="Site">{sites?.items.find((s) => s.id === siteId)?.name ?? "—"}</Summary>
            <Summary label="Channels">{selected.size}</Summary>
            <Summary label="Default profile">{profile.toUpperCase()}</Summary>
            <Summary label="Estimated WAN">{estimate.mbps} Mbps</Summary>
            <Summary label="Outputs">
              {outputs.filter((o) => o.enabled).length === 0
                ? "none"
                : outputs
                    .filter((o) => o.enabled)
                    .map((o) => o.type.toUpperCase())
                    .join(", ")}
            </Summary>
            <Summary label="Billing start">
              {endpoint.first_active_at ? "already active" : "on first check-in from the field"}
            </Summary>
          </dl>

          {!endpoint.nic_roles_json?.lan?.mac && outputs.some((o) => o.type === "qam" && o.enabled) && (
            <div className="rounded-md border border-state-warn/40 bg-state-warn/10 p-3 text-xs text-state-warn">
              This Outpost has no LAN role assigned, so its QAM output will not be rendered. Assign NIC roles
              on the Config tab before shipping.
            </div>
          )}

          <p className="text-xs text-ink-400">
            Activating mints the entitlements and a new signed config version. The Outpost applies it on its
            next check-in, within 30 seconds of being online.
          </p>
        </div>
      )}
    </Modal>
  );
}

function Summary({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-ink-400">{label}</dt>
      <dd className="mt-0.5 text-ink-100">{children}</dd>
    </div>
  );
}

export function OutputsStep({
  outputs,
  setOutputs,
  selected,
  catalog,
}: {
  outputs: OutputSpec[];
  setOutputs: (o: OutputSpec[]) => void;
  selected: number[];
  catalog: Channel[];
}) {
  const hls = outputs.find((o) => o.type === "hls");
  const qamOutputs = outputs.filter((o) => o.type === "qam");
  const srtOutputs = outputs.filter((o) => o.type === "srt");

  const setHls = (enabled: boolean) =>
    setOutputs([...outputs.filter((o) => o.type !== "hls"), { type: "hls", channel_id: null, params: { enabled }, enabled }]);

  // Auto-numbering helper (doc 03 §3.3 step 5): one group per channel, so the
  // operator fills in a base and gets a coherent plan rather than 30 fields.
  const autoNumberQam = (base: string, port: number, tsRate: number, videoKbps: number) => {
    const [a, b, c] = base.split(".").map(Number);
    const next = selected.map((channelId, index) => {
      const last = index + 1;
      return {
        type: "qam" as const,
        channel_id: channelId,
        enabled: true,
        params: {
          mcast: `${a}.${b}.${c}.${last}`,
          port,
          ts_rate: tsRate,
          video_kbps: videoKbps,
          program: index + 1,
          interlace: false,
        },
      };
    });
    setOutputs([...outputs.filter((o) => o.type !== "qam"), ...next]);
  };

  return (
    <div className="space-y-5">
      <section className="rounded-md border border-ink-600 p-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" className="accent-brand" checked={!!hls?.enabled} onChange={(e) => setHls(e.target.checked)} />
          Local HLS on the site LAN
        </label>
        <p className="mt-1 text-xs text-ink-400">
          Served at http://&lt;lan&gt;:8080/ch/&lt;number&gt;/index.m3u8. Uses the fMP4 variant, which is what
          carries HEVC.
        </p>
      </section>

      <section className="rounded-md border border-ink-600 p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">QAM multicast</div>
          <Badge tone={qamOutputs.length ? "brand" : "idle"}>{qamOutputs.length} configured</Badge>
        </div>
        <p className="mt-1 text-xs text-ink-400">
          HEVC decode to MPEG-2 CBR on the LAN NIC, one program per channel.
        </p>
        <QamAutoNumber onApply={autoNumberQam} disabled={selected.length === 0} />
        {qamOutputs.length > 0 && (
          <div className="mt-3 max-h-40 overflow-y-auto rounded border border-ink-700">
            <table className="table text-xs">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Group</th>
                  <th>Port</th>
                  <th>Program</th>
                  <th>TS rate</th>
                </tr>
              </thead>
              <tbody>
                {qamOutputs.map((output) => (
                  <tr key={output.channel_id}>
                    <td>{catalog.find((c) => c.id === output.channel_id)?.name ?? output.channel_id}</td>
                    <td className="font-mono">{String(output.params.mcast)}</td>
                    <td className="tabular-nums">{String(output.params.port)}</td>
                    <td className="tabular-nums">{String(output.params.program)}</td>
                    <td className="tabular-nums">{String(output.params.ts_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-md border border-ink-600 p-4">
        <div className="text-sm font-medium">SRT re-publish</div>
        <SrtEditor
          outputs={srtOutputs}
          onChange={(next) => setOutputs([...outputs.filter((o) => o.type !== "srt"), ...next])}
          catalog={catalog.filter((c) => selected.includes(c.id))}
        />
      </section>
    </div>
  );
}

function QamAutoNumber({
  onApply,
  disabled,
}: {
  onApply: (base: string, port: number, tsRate: number, videoKbps: number) => void;
  disabled: boolean;
}) {
  const [base, setBase] = useState("239.10.1.1");
  const [port, setPort] = useState(5000);
  const [tsRate, setTsRate] = useState(6000000);
  const [videoKbps, setVideoKbps] = useState(5000);

  return (
    <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
      <Field label="Base group">
        <input className="input" value={base} onChange={(e) => setBase(e.target.value)} />
      </Field>
      <Field label="Port">
        <input className="input" type="number" value={port} onChange={(e) => setPort(Number(e.target.value))} />
      </Field>
      <Field label="TS mux rate">
        <input className="input" type="number" value={tsRate} onChange={(e) => setTsRate(Number(e.target.value))} />
      </Field>
      <Field label="Video kbps">
        <input className="input" type="number" value={videoKbps} onChange={(e) => setVideoKbps(Number(e.target.value))} />
      </Field>
      <div className="flex items-end">
        <button
          className="btn-secondary w-full"
          disabled={disabled}
          onClick={() => onApply(base, port, tsRate, videoKbps)}
        >
          Auto-number
        </button>
      </div>
    </div>
  );
}

function SrtEditor({
  outputs,
  onChange,
  catalog,
}: {
  outputs: OutputSpec[];
  onChange: (next: OutputSpec[]) => void;
  catalog: Channel[];
}) {
  const add = () =>
    onChange([
      ...outputs,
      {
        type: "srt",
        channel_id: catalog[0]?.id ?? null,
        enabled: true,
        params: { mode: "caller", host: "", port: 9000, latency_ms: 120, passphrase: "" },
      },
    ]);

  const update = (index: number, params: Record<string, unknown>) =>
    onChange(outputs.map((o, i) => (i === index ? { ...o, params: { ...o.params, ...params } } : o)));

  return (
    <div className="mt-2 space-y-3">
      {outputs.map((output, index) => (
        <div key={index} className="grid grid-cols-2 gap-3 rounded border border-ink-700 p-3 md:grid-cols-5">
          <Field label="Channel">
            <select
              className="input"
              value={output.channel_id ?? ""}
              onChange={(e) =>
                onChange(outputs.map((o, i) => (i === index ? { ...o, channel_id: Number(e.target.value) } : o)))
              }
            >
              {catalog.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.number} · {channel.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Mode">
            <select className="input" value={String(output.params.mode)} onChange={(e) => update(index, { mode: e.target.value })}>
              <option value="caller">caller (dials out)</option>
              <option value="listener">listener (site LAN)</option>
            </select>
          </Field>
          <Field label="Host">
            <input
              className="input"
              disabled={output.params.mode === "listener"}
              value={String(output.params.host ?? "")}
              onChange={(e) => update(index, { host: e.target.value })}
            />
          </Field>
          <Field label="Port">
            <input
              className="input"
              type="number"
              value={Number(output.params.port ?? 9000)}
              onChange={(e) => update(index, { port: Number(e.target.value) })}
            />
          </Field>
          <div className="flex items-end gap-2">
            <Field label="Passphrase">
              <input
                className="input"
                type="password"
                placeholder="optional"
                value={String(output.params.passphrase ?? "")}
                onChange={(e) => update(index, { passphrase: e.target.value })}
              />
            </Field>
            <button className="btn-ghost mb-0.5" onClick={() => onChange(outputs.filter((_, i) => i !== index))}>
              ✕
            </button>
          </div>
        </div>
      ))}
      <button className="btn-secondary" onClick={add} disabled={catalog.length === 0}>
        Add SRT output
      </button>
      {outputs.some((o) => o.params.passphrase && String(o.params.passphrase).length < 10) && (
        <div className="text-xs text-state-warn">
          <StatusDot tone="warn" label="An SRT passphrase must be at least 10 characters." />
        </div>
      )}
    </div>
  );
}
